// Lara BASIC mode — no-nonsense, plain-English version of the same analysis (no extra AI run).
import { toast } from "sonner";
import { Copy, HelpCircle, MessageCircle, TrendingUp, Trophy, Truck, MapPin, Wrench } from "lucide-react";

const list = (x) => (Array.isArray(x) ? x.filter(Boolean) : []);
const clean = (t) => String(t || "").trim().replace(/^["“”']+|["“”']+$/g, "");

// Older analyses have no `basic` block — cut it down from the full one.
function derive(d) {
  if (d.basic) return d.basic;
  return {
    where_project_is: [d.build?.type, d.build?.phase].filter(Boolean).join(" — "),
    how_its_going_in: list(d.products).slice(0, 3).map((p) => `${p.name}: ${p.where_fitted || p.used_for || ""}`),
    ask: list(d.key_questions).slice(0, 5),
    say: [d.call_plan?.opener, ...list(d.upsells).map((u) => u.say_this)].filter(Boolean).slice(0, 3),
    upsell: list(d.upsells).filter((u) => u.size !== "small").slice(0, 3).map((u) => `${u.item} — ${u.why || ""}`),
    win_tips: list(d.coach_tips).slice(0, 3).map((c) => c.how_to_apply || c.principle),
    delivery_checks: ["Is there clear access for the lorry?", "Who will be on site to sign for it?", "Where should we drop the goods?"],
  };
}

function Block({ id, icon: Icon, title, items, tone = "text-slate-100", copyable }) {
  const rows = list(items).map(clean);
  if (!rows.length) return null;
  return (
    <section className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5" data-testid={`lara-basic-${id}`}>
      <div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4 text-amber-300" /><span className="text-[13px] font-bold text-white">{title}</span></div>
      <ul className="space-y-1.5">
        {rows.map((r, i) => (
          <li key={i} className={`flex items-start gap-2 text-[13.5px] leading-snug ${tone}`}>
            <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
            <span className="flex-1">{r}</span>
            {copyable && <button type="button" onClick={() => { navigator.clipboard?.writeText(r); toast.success("Copied"); }} className="text-slate-500 hover:text-white" title="Copy"><Copy className="w-3.5 h-3.5" /></button>}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function LaraBasic({ d }) {
  const b = derive(d || {});
  return (
    <div className="space-y-3" data-testid="lara-basic">
      {b.where_project_is && (
        <div className="rounded-xl border border-violet-400/30 bg-violet-500/[0.08] p-3.5 flex gap-2" data-testid="lara-basic-where">
          <MapPin className="w-4 h-4 text-violet-300 mt-0.5 shrink-0" />
          <div className="text-[14px] text-white font-medium">{clean(b.where_project_is)}</div>
        </div>
      )}
      <Block id="ask" icon={HelpCircle} title="Ask these" items={b.ask} copyable />
      <Block id="say" icon={MessageCircle} title="Say this" items={b.say} tone="text-amber-100" copyable />
      <Block id="upsell" icon={TrendingUp} title="Offer these (big items)" items={b.upsell} tone="text-emerald-100" />
      <Block id="how" icon={Wrench} title="How it's going in" items={b.how_its_going_in} />
      <Block id="win" icon={Trophy} title="To win this order" items={b.win_tips} />
      <Block id="delivery" icon={Truck} title="Before delivery, check" items={b.delivery_checks} />
    </div>
  );
}
